// ============================================
// Authenticated Fetch Helper (ES6)
// Firebase Auth 토큰을 포함한 API 호출
// ============================================

import { getCurrentUser } from '../app/state.js';

/**
 * Firebase Auth ID 토큰 가져오기
 * @returns {Promise<string|null>} ID Token 또는 null
 */
export async function getAuthToken() {
  const user = getCurrentUser();
  if (!user) {
    console.warn('[auth-fetch] 로그인되지 않음');
    return null;
  }

  try {
    const token = await user.getIdToken();
    return token;
  } catch (error) {
    console.error('[auth-fetch] 토큰 가져오기 실패:', error);
    return null;
  }
}

/**
 * 인증된 fetch 요청
 * - Authorization 헤더에 Bearer 토큰 자동 추가
 * - userId를 body에서 제거 (서버에서 토큰으로 확인)
 *
 * @param {string} url - API 엔드포인트 URL
 * @param {Object} options - fetch 옵션
 * @param {Object} options.body - 요청 body (JSON 객체)
 * @returns {Promise<Response>} fetch Response
 */
export async function authFetch(url, options = {}) {
  const token = await getAuthToken();

  if (!token) {
    throw new Error('로그인이 필요합니다');
  }

  // 헤더 설정
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
    ...options.headers
  };

  // body에서 userId 제거 (서버에서 토큰으로 확인)
  let body = options.body;
  if (body && typeof body === 'string') {
    try {
      const parsed = JSON.parse(body);
      delete parsed.userId;  // userId 제거
      body = JSON.stringify(parsed);
    } catch (e) {
      // JSON 파싱 실패 시 그대로 사용
    }
  } else if (body && typeof body === 'object') {
    const { userId, ...rest } = body;
    body = JSON.stringify(rest);
  }

  return fetch(url, {
    ...options,
    method: options.method || 'POST',
    headers,
    body,
    signal: options.signal
  });
}

/**
 * 인증 없이 fetch 요청 (공개 API용)
 * @param {string} url - API 엔드포인트 URL
 * @param {Object} options - fetch 옵션
 * @returns {Promise<Response>} fetch Response
 */
export async function publicFetch(url, options = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers
  };

  let body = options.body;
  if (body && typeof body === 'object' && !(body instanceof FormData)) {
    body = JSON.stringify(body);
  }

  return fetch(url, {
    ...options,
    method: options.method || 'POST',
    headers,
    body
  });
}
